import { backupExportBytes } from '@chorepop/core/client';
import { save } from '@tauri-apps/plugin-dialog';
import { writeFile } from '@tauri-apps/plugin-fs';
import { type Component, Show, createSignal } from 'solid-js';

export const FamilyDataDownloadPanel: Component<{ sessionId: string }> = (props) => {
  const [expanded, setExpanded] = createSignal(false);
  const [password, setPassword] = createSignal('');
  const [confirmation, setConfirmation] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [message, setMessage] = createSignal<string | null>(null);
  const [error, setError] = createSignal<string | null>(null);

  const submit = async (event: Event) => {
    event.preventDefault();
    setError(null);
    setMessage(null);
    if (password().length < 10) {
      setError('Choose a backup password with at least 10 characters.');
      return;
    }
    if (password() !== confirmation()) {
      setError('The backup passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      const destination = await save({
        title: 'Download my family data',
        defaultPath: `ChorePop-family-${new Date().toISOString().slice(0, 10)}.pldb-backup`,
        filters: [{ name: 'Encrypted ChorePop backup', extensions: ['pldb-backup'] }],
      });
      if (!destination) return;
      const exported = await backupExportBytes({
        sessionId: props.sessionId,
        backupPassword: password(),
      });
      if (!exported.ok) throw new Error(exported.error.message);
      if (exported.value.outcome === 'weak_password') {
        throw new Error(
          `Choose a backup password with at least ${exported.value.minLength} characters.`,
        );
      }
      await writeFile(destination, Uint8Array.from(exported.value.archiveBytes));
      setPassword('');
      setConfirmation('');
      setMessage('Your encrypted family backup was saved. Keep its password somewhere safe.');
      setExpanded(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not download the family backup.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section class="settings-section settings-download-section" aria-label="Download family data">
      <h2>Download my family data</h2>
      <p class="settings-section-intro">
        Save an encrypted ChorePop backup that only opens with the password you choose.
      </p>
      <Show when={message()}>
        {(text) => (
          <p class="form-success" role="status">
            {text()}
          </p>
        )}
      </Show>
      <Show
        when={expanded()}
        fallback={
          <button
            type="button"
            class="secondary"
            onClick={() => setExpanded(true)}
            data-testid="family-data-download-open"
          >
            Download my family data
          </button>
        }
      >
        <form class="privacy-delete-form" onSubmit={submit}>
          <label class="field">
            <span class="field-label">Backup password</span>
            <input
              type="password"
              autocomplete="new-password"
              value={password()}
              onInput={(event) => setPassword(event.currentTarget.value)}
              disabled={busy()}
              data-testid="family-data-download-password"
            />
          </label>
          <label class="field">
            <span class="field-label">Confirm backup password</span>
            <input
              type="password"
              autocomplete="new-password"
              value={confirmation()}
              onInput={(event) => setConfirmation(event.currentTarget.value)}
              disabled={busy()}
              data-testid="family-data-download-confirmation"
            />
          </label>
          <Show when={error()}>
            {(text) => (
              <p class="form-error" role="alert">
                {text()}
              </p>
            )}
          </Show>
          <div class="privacy-delete-actions">
            <button
              type="button"
              class="ghost"
              disabled={busy()}
              onClick={() => setExpanded(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              class="primary"
              disabled={busy()}
              data-testid="family-data-download-submit"
            >
              {busy() ? 'Encrypting…' : 'Choose where to save'}
            </button>
          </div>
        </form>
      </Show>
    </section>
  );
};
